import { Box, Card, CardContent, Grid, Skeleton, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetOperationalKPIsResponse, OperationalKPIMetric } from "subModule/src/services/analytics/interface";
import { useGetOperationalKPIsService } from "subModule/src/services/analytics/useAnalytics";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";
interface OperationalKPIsProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: { range: any };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer": {
      background: "#fff",
      padding: 0,
      width: "15px",
      height: "15px",
    },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": { position: "absolute", right: 0 },
    border: 0,
  },
};
const getStatusStyles = (statusVariant: string) => {
  switch (statusVariant) {
    case "success":
      return { backgroundColor: "#D1E5DB", color: "#16A34A" };
    case "warning":
      return { backgroundColor: "#FEF3C7", color: "#D97706" };
    case "error":
    case "critical":
      return { backgroundColor: "#FEE2E2", color: "#DC2626" };
    default:
      return { backgroundColor: "#F3F4F6", color: "#6B7280" };
  }
}
const formatMetricDisplay = (metric: OperationalKPIMetric) => {
  const value = Math.abs(metric.value || 0);
  if (metric.unit === "percentage") {
    return `${new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value)}%`;
  }
  if (metric.unit === "currency") {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(value);
};
const formatMetricTooltip = (metric: OperationalKPIMetric) => {
  const value = metric.value ?? 0;
  if (metric.unit === "percentage") {
    return `${value.toLocaleString()}%`;
  }
  if (metric.unit === "currency") {
    return (metric.value ?? 0).toLocaleString();
  }
  return value.toLocaleString();
};
const KpiMetricCard = ({ metric, label }: { metric: OperationalKPIMetric; label: string }) => {
  const footer = metric.trend?.label
    ? { text: metric.trend.label, statusVariant: metric.trend.statusVariant }
    : metric.badge
      ? { text: metric.badge.text, statusVariant: metric.badge.statusVariant }
      : null;
  const footerStyles = footer ? getStatusStyles(footer.statusVariant) : null;
  const displayValue = formatMetricDisplay(metric);
  const tooltipValue = formatMetricTooltip(metric);
  return (
    <Box
      sx={{
        backgroundColor: "#FAF8F5",
        borderRadius: "8px",
        padding: "12px",
        height: "100%",
        minHeight: "88px",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
      }}
    >
      <Tooltip followCursor title={label} enterDelay={0}>
        <Typography className="font-Saira truncate" sx={{ fontSize: '10px', color: "#6B7280", fontWeight: 500 }}>
          {label}
        </Typography>
      </Tooltip>
      <Tooltip followCursor title={tooltipValue} enterDelay={0}>
        <Typography
          className="font-Saira truncate text-center"
          sx={{ fontSize: '19px', fontWeight: 700, color: "#111827", lineHeight: 1.2 }}
        >
          {metric.unit === "currency" && metric.currency ? (
            <>
              {metric.currency}{" "}{(metric.value ?? 0) < 0 ? "-" : ""}{displayValue.replace(metric.currency, "").replace(/^-/, "")}
            </>
          ) : displayValue}
        </Typography>
      </Tooltip>
      {metric.subtitle && !footer && (
        <Typography className="font-Saira truncate" sx={{ fontSize: '9px', color: "#9CA3AF", marginTop: "2px" }}>
          {metric.subtitle}
        </Typography>
      )}
      {footer && footerStyles && (
        <Box
          component="span"
          sx={{
            display: "block",
            alignSelf: "flex-start",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: '8px',
            fontWeight: 500,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            margin: "4px auto 0 auto",
            ...footerStyles,
          }}
        >
          <Tooltip followCursor title={footer.text} enterDelay={0}>
            <span>{footer.text}</span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
export default function OperationalKPIs(props: OperationalKPIsProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<GetOperationalKPIsResponse["data"] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [stationsLoaded, setStationsLoaded] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { data: stationsData, refetch: stationsRefetch } = useGetStationDropdownService({
    pageNumber: pageNo,
    size: 50,
    name: dropdownSearchTerm,
  });
  const { control, formState: { errors }, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: 'monthly', label: t('mtd') },
    },
  });
  const filters = useMemo(() => [
    { value: 'daily', label: t('today') },
        { value: 'yesterday', label: t('yesterday') },
    { value: 'weekly', label: t('wtd') },
    { value: 'monthly', label: t('mtd') },
    { value: 'last_month', label: t('last_month') },
    { value: 'yearly', label: t('ytd') },
  ], [i18n.language]);
  const values = watch(["stationId", "range"]);
  const selectedStationValue = Number(values[0]?.value ?? values[0]);
  const isAllStationSelected = selectedStationValue === 0;
  const { refetch: apiRefetch } = useGetOperationalKPIsService({
    stationId: isAllStationSelected ? null : (values[0]?.value || null),
    allStation: isAllStationSelected,
    range: values[1]?.value || null,
  });
  const stations = useMemo(() => {
    const stationOptions = (stationsData?.pages[0]?.data?.content || []).map((item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name,
    }));
    if (stationOptions.length > 1) stationOptions.splice(0, 0, { value: 0, label: t("allStations") });
    return stationOptions;
  }, [stationsData, i18n.language]);
  useEffect(() => {
    const getStationsData = setTimeout(async () => {
      await stationsRefetch();
      setStationsLoaded(true);
    }, 50);
    return () => clearTimeout(getStationsData);
  }, []);
  useEffect(() => {
    if (firstRender.current && stationsLoaded && stations.length > 0) {
      firstRender.current = false;
      const defaultStation = (stations.length > 1 ? stations[1] : stations[0]) || null;
      setValue("stationId", defaultStation);
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 50);
    }
  }, [stations, stationsLoaded]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find(f => f.value === currentRange?.value);
    if (updated) setValue("range", updated);
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const handleDropdownActions = (typeName: string, action: "searchIt" | "loadNext", search?: string) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search || "");
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => stationsRefetch().finally(() => setSearchLoading(null)), 300);
  };
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || null);
        } else {
          props.enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true) props.setGlobalFilterApplied(false);
      });
  };
  const metrics = data?.metrics || [];
  return (
    <Card sx={{ height: "285px", boxShadow: "none", borderRadius: "8px", border: "1px solid #E5E7EB", "& .MuiCardContent-root": { padding: "16px !important", height: "100%", boxSizing: "border-box" } }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={dashboardWidgetHeaderSx}>
          <Box sx={dashboardWidgetTitleRowSx}>
                    <Typography
            variant="h1"
            gutterBottom
            className="font-Saira text-start mb-0 mt-[5px]" sx={{ color: props.themes?.primary, fontSize: { xs: 12, sm: 14 }, fontWeight: { xs: 600, sm: 600 }, textAlign: "left" }}
          >
            {t("operationalKPIs")}
          </Typography>
                    </Box>
                    <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={stations}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={searchLoading === "operationalKPIs"}
                    isSearchable
                    onInputChange={(search: string, { action }: { action: string }) => {
                      if (action === "input-change" || (action !== "set-value" && action !== "menu-close" && action !== "input-blur")) {
                        handleDropdownActions("operationalKPIs", "searchIt", search);
                      } else if (action === "input-blur" || action === "menu-close") {
                        if (dropdownSearchTerm || pageNo > 1) handleDropdownActions("operationalKPIs", "searchIt", "");
                        else setDropdownSearchTerm("");
                        setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(stationsData?.pages[0]?.data as any)?.last) handleDropdownActions("operationalKPIs", "loadNext");
                    }}
                    page={pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, overflow: "hidden", marginTop: "5px" }}>
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
          </Box>
        ) : metrics.length === 0 ? (
          <Box className="flex justify-center flex-col flex-1">
            <Typography className="font-Saira text-center italic font-semibold" sx={{ color: props.themes.primary }}>
              {t("noRecordFound")}
            </Typography>
          </Box>
        ) : (
          <Box className="max-h-[220px] overflow-y-auto mt-[8px]">
            <Grid container spacing={1.5}>
              {metrics.map((metric) => (
                <Grid item xs={6} key={metric.key}>
                  <KpiMetricCard metric={metric} label={t(metric.key) || metric.label} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
